import { inject, Injectable } from '@angular/core';
import { catchError, from, map, Observable, throwError } from 'rxjs';
import PouchDB from 'pouchdb';
import PouchDBFind from 'pouchdb-find';
import AuthService from '../services/auth.service';
import { DomSanitizer } from '@angular/platform-browser';

PouchDB.plugin(PouchDBFind);
export interface FormStepDefinition {
  id: number;
  fields: FieldDefinition[];
  title?: string;
  logic?: {
    skip?: {
      condition: string;
      goto_step: number;
    };
  };
}

export interface FieldDefinition {
  type: string;
  action?: { type?: string; step?: string };
  items?: any;
  name: string;
  label: string;
  inputName?: string;
  helper?: string;
  logic?: any;
}

export interface FormDefinition {
  icon?: string;
  _id: string;
  name: string;
  type: string;
  nextForms?: string[];
  steps: FormStepDefinition[];
  in_bottom?: boolean;
}

export interface FormResponseDoc {
  _id: string;
  type: string;
  createdAt: string;
  status: 'draft';
  data: any;
}

@Injectable({
  providedIn: 'root',
})
export default class FormService {
  private db = new PouchDB('forms');
  private auth = inject(AuthService);
  private userDb = new PouchDB('user_' + this.auth.currentUser?.uuid);
  private sanitize = inject(DomSanitizer);

  constructor() {
    this.setupIndexes();
  }

  private async setupIndexes() {
    await this.db.createIndex({ index: { fields: ['type'] } });
  }

  /**
   * Initialize form in couchdb with status 'draft'
   * @returns form _id
   */
  startForm(formDefinitionId: string) {
    const newId = `resp_${formDefinitionId}_${Date.now()}`;
    const newDoc = {
      _id: newId,
      type: 'response',
      createdAt: new Date().toISOString(),
      status: 'draft',
      data: {},
    };

    return from(this.userDb.put(newDoc)).pipe(map((response) => response.id));
  }

  async setFinish(responseId: string) {
    try {
      const doc: any = await this.userDb.get(responseId);

      doc.status = 'finish';

      return await this.userDb.put(doc);
    } catch (err) {
      console.error('Erreur lors de la sauvegarde CouchDB', err);
      throw err;
    }
  }

  async saveData(
    responseId: string,
    newData: any,
    finished: boolean,
    formDescriptorId: string,
    photos: { [key: string]: { file: File; url: string }[] },
  ) {
    try {
      const doc: any = await this.userDb.get(responseId);

      if (finished) doc.status = 'finished';

      doc.author_uuid = this.auth.currentUser?.uuid.toString();
      doc.author_name = this.auth.currentUser?.name;
      doc.created_at = new Date();
      doc.formDescriptorId = formDescriptorId;

      doc.data = {
        ...doc.data,
        ...newData,
      };

      const saved = await this.userDb.put(doc);

      let currentRev = saved.rev;
      if (Object.values(photos).some((tableau) => tableau.length > 0)) {
        for (let fieldName of Object.keys(photos)) {
          const fieldPhoto = photos[fieldName];

          for (let i = 0; i < fieldPhoto.length; i++) {
            const photo = fieldPhoto[i];
            const attachmentRes = await this.userDb.putAttachment(
              doc._id,
              `${fieldName}_${i}`,
              currentRev,
              photo.file,
              photo.file.type,
            );

            currentRev = attachmentRes.rev;
          }
        }
      }
    } catch (err) {
      console.error('Erreur lors de la sauvegarde CouchDB', err);
      throw err;
    }
  }

  getForms(): Observable<{ name: string; id: string }[]> {
    const promise = this.db
      .find({
        selector: { type: 'form:definition', shortcut: true },
      })
      .then((res) => res.docs as unknown as FormDefinition[]);

    return from(promise).pipe(
      map((forms: FormDefinition[]) =>
        forms.map((f) => {
          return {
            name: f.name,
            icon: f.icon ? this.sanitize.bypassSecurityTrustHtml(f.icon) : undefined,
            id: f._id,
            in_bottom: f.in_bottom,
          };
        }),
      ),
    );
  }

  findMany(ids: string[]) {
    const promise = this.db
      .find({
        selector: { type: 'form:definition', _id: { $in: ids } },
      })
      .then((res) => res.docs as unknown as FormDefinition[]);

    return from(promise).pipe(
      map((forms: FormDefinition[]) =>
        forms.map((f) => ({
          name: f.name,
          _id: f._id,
        })),
      ),
    );
  }

  getFormById(id: string): Observable<FormDefinition> {
    return from(this.db.get<FormDefinition>(id)).pipe(
      catchError((err) => {
        console.error(`Erreur lors de la récupération du formulaire ${id}`, err);
        return throwError(() => new Error('Formulaire introuvable'));
      }),
    );
  }
}
